/**
 * AISuggestionsPanel — Neural Insights (Aether Command aesthetic)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { GlassCard } from './ui/glass-card';
import {
  Lock, TrendingUp, TrendingDown, AlertTriangle, Users, Package,
  CreditCard, Calendar, Loader2, Brain, RefreshCw, BarChart3
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_AVATAR = {
  growth:   { icon: TrendingUp,    bg: '#1c1b1b', iconColor: '#62FF96' },
  decline:  { icon: TrendingDown,  bg: '#ff571a', iconColor: '#FFFFFF' },
  credit:   { icon: CreditCard,    bg: '#1c1b1b', iconColor: '#F59E0B' },
  stock:    { icon: Package,       bg: '#1c1b1b', iconColor: '#F59E0B' },
  customer: { icon: Users,         bg: '#1c1b1b', iconColor: '#06B6D4' },
  seasonal: { icon: Calendar,      bg: '#1c1b1b', iconColor: '#F472B6' },
  alert:    { icon: AlertTriangle, bg: '#1c1b1b', iconColor: '#F59E0B' },
  default:  { icon: Brain,         bg: '#e2e2e2', iconColor: '#2f3131' },
};

const AISuggestionsPanel = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dismissedIds, setDismissedIds] = useState([]);

  const isPremium = user?.current_plan === 'premium' || user?.current_plan === 'premium_trial';

  useEffect(() => {
    if (isPremium) {
      fetchSuggestions();
      const dismissed = JSON.parse(localStorage.getItem('dismissed_suggestions') || '[]');
      setDismissedIds(dismissed);
    } else setLoading(false);
  }, [isPremium]);

  const fetchSuggestions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/suggestions`, { withCredentials: true });
      setSuggestions(res.data.suggestions || getMockSuggestions());
    } catch { setSuggestions(getMockSuggestions()); }
    setLoading(false);
  };

  const refreshSuggestions = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/suggestions/regenerate`, {}, { withCredentials: true });
      setSuggestions(res.data.suggestions || getMockSuggestions());
    } catch { setSuggestions(getMockSuggestions()); }
    setRefreshing(false);
  };

  const dismissSuggestion = (id) => {
    const newDismissed = [...dismissedIds, id];
    setDismissedIds(newDismissed);
    localStorage.setItem('dismissed_suggestions', JSON.stringify(newDismissed));
    axios.post(`${API_URL}/api/ai/suggestions/${id}/dismiss`, {}, { withCredentials: true }).catch(() => {});
  };

  const getMockSuggestions = () => [
    { id: 'sug_1', type: 'credit', message: language === 'hi' ? '₹5,000+ बकाया 30 दिनों से ज्यादा से' : '₹5,000+ pending for over 30 days — consolidating overdue ledgers could improve cash flow by 12% this quarter.', action_label: language === 'hi' ? 'रिमाइंडर भेजें' : 'Send Reminders', action_url: '/credit?filter=overdue' },
    { id: 'sug_2', type: 'stock', message: language === 'hi' ? '3 आइटम्स का स्टॉक कम है' : '3 items running low on stock — projected stockout in 4-5 days based on current sales velocity.', action_label: language === 'hi' ? 'अभी रीऑर्डर करें' : 'Priority Reorder', action_url: '/stock?filter=low' },
  ];

  const visibleSuggestions = suggestions.filter(s => !dismissedIds.includes(s.id));

  if (!isPremium) {
    return (
      <GlassCard hoverable={false} stripe={false} className="p-6 md:p-8 lg:col-span-2 text-center">
        <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-3">
          <Lock className="h-6 w-6 text-white/40" />
        </div>
        <h3 className="headline-sm text-white mb-1">{language === 'hi' ? 'AI सुझाव' : 'AI Insights'}</h3>
        <p className="text-sm text-white/55 mb-4">{language === 'hi' ? 'Premium पर अपग्रेड करें' : 'Upgrade to Premium to unlock AI Insights'}</p>
        <Button
          style={{ backgroundColor: '#FFFFFF', color: '#0A0A0B' }}
          onClick={() => navigate('/payment?type=upgrade')}
          className="hover:opacity-90 rounded-lg font-bold"
        >
          {language === 'hi' ? 'अपग्रेड करें' : 'Upgrade'}
        </Button>
      </GlassCard>
    );
  }

  return (
    <GlassCard hoverable={false} stripe={false} className="p-6 md:p-8 lg:col-span-2">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="headline-md text-white mb-1">{language === 'hi' ? 'AI सुझाव' : 'AI Insights'}</h2>
          <p className="text-sm text-white/55">{language === 'hi' ? 'आपके व्यवसाय पर AI विश्लेषण' : 'Predictive analysis based on your business performance'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refreshSuggestions} disabled={refreshing}
            className="text-white/40 hover:text-white p-2 rounded-lg transition-colors" data-testid="refresh-insights">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <div className="px-3 py-1 bg-[#62ff96]/10 border border-[#62ff96]/20 rounded-full">
            <span className="text-[10px] font-bold text-[#62ff96] tracking-widest uppercase">{language === 'hi' ? 'लाइव पल्स' : 'Live Pulse'}</span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        ) : visibleSuggestions.length === 0 ? (
          <div className="text-center py-8">
            <BarChart3 className="h-10 w-10 mx-auto text-white/20 mb-2" />
            <p className="text-white/45 text-sm">{language === 'hi' ? 'कोई नई इनसाइट नहीं' : 'No new insights right now'}</p>
          </div>
        ) : (
          visibleSuggestions.slice(0, 3).map((s) => {
            const avatar = TYPE_AVATAR[s.type] || TYPE_AVATAR.default;
            const Icon = avatar.icon;
            return (
              <div
                key={s.id}
                className="group flex gap-5 p-5 rounded-xl transition-all border border-transparent hover:border-[var(--aether-outline-variant)] hover:bg-[var(--aether-surface)]"
              >
                <div
                  className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: avatar.bg, border: `1px solid var(--aether-outline-variant)` }}
                >
                  <Icon className="h-5 w-5" style={{ color: avatar.iconColor }} />
                </div>
                <div className="flex-1">
                  <h4 className="font-bold text-white mb-1 tracking-tight">{s.title || (language === 'hi' ? 'AI सुझाव' : 'AI Recommendation')}</h4>
                  <p className="text-sm text-white/65 leading-relaxed">{s.message}</p>
                  <div className="mt-3 flex gap-4">
                    {s.action_url && (
                      <button onClick={() => navigate(s.action_url)}
                        className="text-xs font-bold text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition-all">
                        {s.action_label || (language === 'hi' ? 'देखें' : 'Deploy Suggestion')}
                      </button>
                    )}
                    <button onClick={() => dismissSuggestion(s.id)}
                      className="text-xs font-bold text-white/55 hover:text-white transition-all">
                      {language === 'hi' ? 'खारिज करें' : 'Dismiss'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </GlassCard>
  );
};

export default AISuggestionsPanel;
